(() => {
  function enhance(root) {
    root.querySelectorAll('.pu-engineering-page').forEach((page) => {
      if (page.dataset.engineeringEnhanced) return;

      const tabs = [...page.querySelectorAll('[data-engineering-tab]')];
      const panels = [...page.querySelectorAll('[data-engineering-panel]')];
      const motionPreference = window.matchMedia('(prefers-reduced-motion: reduce)');
      const videoRegions = [...panels, ...page.querySelectorAll('.pu-engineering-hero')];
      const mediaStates = videoRegions.map((panel) => {
        const video = panel.querySelector('[data-engineering-video]');
        const button = panel.querySelector('[data-engineering-video-toggle]');
        return { panel, video, button, inView: false, userPaused: false, requested: false, failed: false, pending: false };
      }).filter((state) => state.video && state.button);
      const shouldPlay = (state) => !state.panel.hidden && state.inView && !document.hidden && !state.userPaused && !state.failed &&
        (state.requested || (!motionPreference.matches && !navigator.connection?.saveData));
      const updateVideoButton = (state) => {
        const action = state.failed ? 'Retry' : state.video.paused ? 'Play' : 'Pause';
        const name = state.video.dataset.engineeringVideoLabel || tabs.find((tab) => tab.dataset.engineeringTab === state.panel.dataset.engineeringPanel)?.textContent.trim() || '';
        state.button.textContent = `${action} video`;
        state.button.setAttribute('aria-label', `${action} ${name} video`);
      };
      const syncVideo = (state) => {
        if (!shouldPlay(state)) {
          state.video.pause();
          return;
        }
        if (!state.video.paused || state.pending) return;
        if (!state.video.hasAttribute('src')) state.video.src = state.video.dataset.src;
        state.video.muted = true;
        state.pending = true;
        state.video.play().then(() => {
          if (!shouldPlay(state)) state.video.pause();
        }).catch((error) => {
          // A tab/visibility change can interrupt loading; it is not a media failure.
          if (error.name !== 'AbortError') state.userPaused = true;
        }).finally(() => {
          state.pending = false;
          updateVideoButton(state);
        });
      };
      mediaStates.forEach((state) => {
        state.video.muted = true;
        state.button.hidden = false;
        state.video.addEventListener('playing', () => {
          state.video.classList.add('is-playing');
          updateVideoButton(state);
        });
        state.video.addEventListener('pause', () => updateVideoButton(state));
        state.video.addEventListener('error', () => {
          state.failed = true;
          state.video.classList.remove('is-playing');
          updateVideoButton(state);
        });
        state.button.addEventListener('click', () => {
          if (!state.video.paused && !state.failed) {
            state.userPaused = true;
            state.video.pause();
          } else {
            if (state.failed) {
              state.failed = false;
              state.video.removeAttribute('src');
              state.video.load();
            }
            state.userPaused = false;
            state.requested = true;
            state.inView = true;
            syncVideo(state);
          }
        });
      });

      const selectTab = (tab, focus = false) => {
        const key = tab.dataset.engineeringTab;
        tabs.forEach((item) => {
          const selected = item === tab;
          item.setAttribute('aria-selected', String(selected));
          item.classList.toggle('is-active', selected);
          item.tabIndex = selected ? 0 : -1;
        });
        panels.forEach((panel) => {
          panel.hidden = panel.dataset.engineeringPanel !== key;
        });
        mediaStates.forEach(syncVideo);
        if (focus) tab.focus();
      };

      tabs.forEach((tab, index) => {
        tab.addEventListener('click', () => selectTab(tab));
        tab.addEventListener('keydown', (event) => {
          if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
          event.preventDefault();
          let nextIndex = index;
          if (event.key === 'ArrowRight') nextIndex = (index + 1) % tabs.length;
          if (event.key === 'ArrowLeft') nextIndex = (index - 1 + tabs.length) % tabs.length;
          if (event.key === 'Home') nextIndex = 0;
          if (event.key === 'End') nextIndex = tabs.length - 1;
          selectTab(tabs[nextIndex], true);
        });
      });

      if (tabs.length) {
        selectTab(tabs[0]);
        page.querySelector('.pu-engineering-tabs').hidden = false;
      }
      if ('IntersectionObserver' in window) {
        const observer = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            const state = mediaStates.find((item) => item.video.parentElement === entry.target);
            state.inView = entry.isIntersecting;
            syncVideo(state);
          });
        }, { threshold: 0.05 });
        mediaStates.forEach((state) => observer.observe(state.video.parentElement));
      }
      document.addEventListener('visibilitychange', () => mediaStates.forEach(syncVideo));
      motionPreference.addEventListener('change', () => {
        mediaStates.forEach((state) => {
          state.requested = false;
          syncVideo(state);
        });
      });

      const positionsRail = page.querySelector('[data-engineering-positions-rail]');
      const positionsPrevious = page.querySelector('[data-engineering-positions-prev]');
      const positionsNext = page.querySelector('[data-engineering-positions-next]');
      if (positionsRail && positionsPrevious && positionsNext) {
        const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        let dragging = false;
        let moved = false;
        let startX = 0;
        let startScroll = 0;
        let updateFrame = 0;

        const step = () => {
          const card = positionsRail.firstElementChild;
          if (!card) return positionsRail.clientWidth;
          const styles = window.getComputedStyle(positionsRail);
          const gap = parseFloat(styles.columnGap || styles.gap) || 0;
          return card.getBoundingClientRect().width + gap;
        };

        const updateControls = () => {
          updateFrame = 0;
          const maximum = Math.max(0, positionsRail.scrollWidth - positionsRail.clientWidth);
          const edgeTolerance = Math.max(2, step() * 0.12);
          positionsPrevious.disabled = positionsRail.scrollLeft <= 2;
          positionsNext.disabled = positionsRail.scrollLeft >= maximum - edgeTolerance;
        };

        const queueControlUpdate = () => {
          if (updateFrame) return;
          updateFrame = window.requestAnimationFrame(updateControls);
        };

        const move = (direction) => {
          positionsRail.scrollBy({ left: step() * direction, behavior: reducedMotion ? 'auto' : 'smooth' });
        };

        const moveTo = (left) => {
          positionsRail.scrollTo({ left, behavior: reducedMotion ? 'auto' : 'smooth' });
        };

        const endDrag = (event) => {
          if (!dragging) return;
          dragging = false;
          positionsRail.classList.remove('is-dragging');
          if (positionsRail.hasPointerCapture(event.pointerId)) positionsRail.releasePointerCapture(event.pointerId);
          if (moved) moveTo(Math.round(positionsRail.scrollLeft / step()) * step());
        };

        positionsPrevious.addEventListener('click', () => move(-1));
        positionsNext.addEventListener('click', () => move(1));
        positionsRail.addEventListener('scroll', queueControlUpdate, { passive: true });
        positionsRail.addEventListener('keydown', (event) => {
          if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
          event.preventDefault();
          if (event.key === 'ArrowLeft') move(-1);
          if (event.key === 'ArrowRight') move(1);
          if (event.key === 'Home') moveTo(0);
          if (event.key === 'End') moveTo(positionsRail.scrollWidth - positionsRail.clientWidth);
        });
        positionsRail.addEventListener('pointerdown', (event) => {
          if (event.pointerType !== 'mouse' || event.button !== 0) return;
          dragging = true;
          moved = false;
          startX = event.clientX;
          startScroll = positionsRail.scrollLeft;
          positionsRail.classList.add('is-dragging');
          positionsRail.setPointerCapture(event.pointerId);
          positionsRail.focus({ preventScroll: true });
          event.preventDefault();
        });
        positionsRail.addEventListener('pointermove', (event) => {
          if (!dragging) return;
          const distance = startX - event.clientX;
          if (Math.abs(distance) > 5) moved = true;
          positionsRail.scrollLeft = startScroll + distance;
          event.preventDefault();
        });
        positionsRail.addEventListener('pointerup', endDrag);
        positionsRail.addEventListener('pointercancel', endDrag);
        window.addEventListener('resize', queueControlUpdate);
        updateControls();
      }

      page.dataset.engineeringEnhanced = 'true';
    });
  }

  enhance(document);
  document.addEventListener('shopify:section:load', (event) => enhance(event.target));
})();
